import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { LedgerRow } from "../types";

const PAGE_SIZE = 8;

interface LedgerTableProps {
  rows: LedgerRow[];
  isLoading?: boolean;
}

function isEarnType(transactionType: string) {
  const type = transactionType.toLowerCase();
  return type === "earn" || type === "purchase";
}

export function LedgerTable({ rows, isLoading = false }: LedgerTableProps) {
  const [page, setPage] = useState(1);

  // API returns oldest first — reverse so latest appear at the top
  const orderedRows = [...rows].reverse();
  const totalPages = Math.max(1, Math.ceil(orderedRows.length / PAGE_SIZE));

  useEffect(() => {
    setPage(1);
  }, [rows]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageRows = orderedRows.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  const from =
    orderedRows.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, orderedRows.length);

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15, duration: 0.4 }}
      className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm sm:p-6"
    >
      <div className="mb-5">
        <h2 className="text-lg font-semibold tracking-tight text-slate-900">
          Customer Ledger
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Recent member transactions
        </p>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-100">
        <table className="min-w-[480px] w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/80 text-xs font-medium uppercase tracking-wider text-slate-400">
              <th className="px-4 py-3">Counter ID</th>
              <th className="px-4 py-3">Invoice No</th>
              <th className="px-4 py-3">Txn Type</th>
              <th className="px-4 py-3">Invoice Amt</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-8 text-center text-sm text-slate-500"
                >
                  Loading transactions…
                </td>
              </tr>
            ) : orderedRows.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-8 text-center text-sm text-slate-500"
                >
                  No transactions found
                </td>
              </tr>
            ) : (
              pageRows.map((row) => (
                <tr
                  key={row.id}
                  className="transition hover:bg-slate-50/80"
                >
                  <td className="px-4 py-3.5 font-medium text-slate-800">
                    {row.counterId}
                  </td>
                  <td className="px-4 py-3.5 font-mono text-slate-600">
                    {row.invoiceNo}
                  </td>
                  <td className="px-4 py-3.5">
                    <span
                      className={
                        isEarnType(row.transactionType)
                          ? "inline-flex rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-100"
                          : "inline-flex rounded-full bg-rose-50 px-2.5 py-0.5 text-xs font-semibold text-rose-700 ring-1 ring-rose-100"
                      }
                    >
                      {row.transactionType}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 font-medium text-slate-800">
                    {row.invoiceAmt}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!isLoading && orderedRows.length > 0 ? (
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-slate-500">
            Showing {from}–{to} of {orderedRows.length}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Previous
            </button>
            <span className="min-w-[4.5rem] text-center text-xs font-medium text-slate-600">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ) : null}
    </motion.section>
  );
}
